import { WorkTaskPriority } from '@planitgeo/treeplotter-domain/lib/modules/work/work-task/domain/work-task.interface';
import { createSlice } from '@reduxjs/toolkit';

/**
 * Work filter state shape - used by both map and home page filters
 */
export type WorkFiltersState = {
  assignedTo: string;
  taskType: string[];
  /**
   * Status filter - array of selected statuses
   * - Empty array: no filter (show all)
   * - ['open']: show only open tasks
   * - ['completed']: show only completed tasks
   * - ['open', 'completed']: show all tasks (both selected)
   */
  status: ('open' | 'completed')[];
  /**
   * Priority filter - array of selected priorities
   */
  priority: WorkTaskPriority[];
  /**
   * Work order filter - array of selected work order names
   */
  workOrderName: string[];
};

/**
 * Asset (tree) filter state. Applied server-side alongside work filters, and
 * ANDed with them, so it is kept separate rather than folded into WorkFiltersState.
 */
export type AssetFiltersState = {
  /** DBH range in the tenant's configured unit. Empty string means unbounded. */
  dbh: { min: number | ''; max: number | '' };
};

type IUIState = {
  isPhotoSave: boolean;
  isFullscreen: boolean;
  homeContainers: {
    [containerId: string]: boolean;
  };
  expandedHomeContainer: string | null;
  /**
   * Applied work list filters - used by the map sidebar
   * These are the "committed" filters that affect map display
   */
  appliedWorkListFilters: WorkFiltersState;
  /**
   * Home page work filters - used by the home page work list
   * - Home page changes stay local until "View in Map" is clicked
   * - Map filter changes automatically sync here to keep both in sync
   */
  homePageFilters: WorkFiltersState;
  /** Applied asset filters - map only, ANDed with appliedWorkListFilters */
  appliedAssetFilters: AssetFiltersState;
  /** Whether trees outside the active filters are drawn as background context */
  showAllTrees: boolean;
};

const defaultFilters: WorkFiltersState = {
  assignedTo: 'all',
  taskType: [],
  status: ['open'],
  priority: [],
  workOrderName: [],
};

const emptyAssetFilters: AssetFiltersState = {
  dbh: { min: '', max: '' },
};

const emptyFilters: WorkFiltersState = {
  assignedTo: 'all',
  taskType: [],
  status: [],
  priority: [],
  workOrderName: [],
};

const initialState: IUIState = {
  isPhotoSave: false,
  isFullscreen: false,
  homeContainers: {},
  expandedHomeContainer: 'inventory', // Default to inventory expanded
  appliedWorkListFilters: { ...defaultFilters },
  homePageFilters: { ...defaultFilters },
  appliedAssetFilters: { ...emptyAssetFilters },
  showAllTrees: false,
};

const slice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setIsPhotoSave(state: IUIState, action: { payload: { isPhotoSave: boolean } }) {
      return {
        ...state,
        isPhotoSave: action.payload.isPhotoSave,
      };
    },
    setIsFullscreen(state: IUIState, action: { payload: { isFullscreen: boolean } }) {
      return {
        ...state,
        isFullscreen: action.payload.isFullscreen,
      };
    },
    setHomeContainerExpanded(
      state: IUIState,
      action: { payload: { containerId: string; expanded: boolean } }
    ) {
      return {
        ...state,
        homeContainers: {
          ...state.homeContainers,
          [action.payload.containerId]: action.payload.expanded,
        },
      };
    },
    setExpandedHomeContainer(state: IUIState, action: { payload: string | null }) {
      return {
        ...state,
        expandedHomeContainer: action.payload,
      };
    },
    /**
     * Apply map filters - also syncs to home page filters so they stay in sync
     */
    applyWorkListFilters(
      state: IUIState,
      action: {
        payload: Partial<{
          assignedTo: string;
          taskType: string[];
          status: ('open' | 'completed')[];
          priority: WorkTaskPriority[];
          workOrderName: string[];
        }>;
      }
    ) {
      const newFilters = {
        ...state.appliedWorkListFilters,
        ...action.payload,
      };
      return {
        ...state,
        appliedWorkListFilters: newFilters,
        // Also update home page filters so they stay in sync with map changes
        homePageFilters: newFilters,
      };
    },
    applyAssetFilters(state: IUIState, action: { payload: Partial<AssetFiltersState> }) {
      return {
        ...state,
        appliedAssetFilters: {
          ...state.appliedAssetFilters,
          ...action.payload,
        },
      };
    },
    setShowAllTrees(state: IUIState, action: { payload: boolean }) {
      return { ...state, showAllTrees: action.payload };
    },
    clearAppliedAssetFilters(state: IUIState) {
      return {
        ...state,
        appliedAssetFilters: { ...emptyAssetFilters },
      };
    },
    /**
     * Clear map filters - also syncs to home page filters
     */
    clearAppliedWorkListFilters(state: IUIState) {
      return {
        ...state,
        appliedWorkListFilters: { ...emptyFilters },
        // Also clear home page filters so they stay in sync
        homePageFilters: { ...emptyFilters },
      };
    },
    /**
     * Clear only the applied map filters, leaving home page filters intact.
     *
     * Used when the user opens the map from the main bottom navigation: the map
     * must open unfiltered (showing all assets), but the user's home work-list
     * filter selections should be preserved. Intentionally does NOT keep the two
     * filter sets in sync (unlike clearAppliedWorkListFilters).
     */
    clearAppliedWorkListFiltersForMapNav(state: IUIState) {
      return {
        ...state,
        appliedWorkListFilters: { ...emptyFilters },
      };
    },
    /**
     * Update home page filters (partial updates)
     */
    updateHomePageFilters(state: IUIState, action: { payload: Partial<WorkFiltersState> }) {
      return {
        ...state,
        homePageFilters: {
          ...state.homePageFilters,
          ...action.payload,
        },
      };
    },
    /**
     * Clear home page filters to defaults
     */
    clearHomePageFilters(state: IUIState) {
      return {
        ...state,
        homePageFilters: { ...emptyFilters },
      };
    },
    /**
     * Sync home page filters to applied filters (called when "View in Map" is clicked)
     */
    syncHomeFiltersToMap(state: IUIState) {
      return {
        ...state,
        appliedWorkListFilters: {
          ...state.homePageFilters,
        },
      };
    },
  },
});

const { reducer } = slice;
const {
  setIsPhotoSave,
  setIsFullscreen,
  setHomeContainerExpanded,
  setExpandedHomeContainer,
  applyWorkListFilters,
  applyAssetFilters,
  clearAppliedAssetFilters,
  setShowAllTrees,
  clearAppliedWorkListFilters,
  clearAppliedWorkListFiltersForMapNav,
  updateHomePageFilters,
  clearHomePageFilters,
  syncHomeFiltersToMap,
} = slice.actions;

if (!reducer) {
  throw new Error('No reducer found for ui');
}

// eslint-disable-next-line import/no-unused-modules
export {
  reducer,
  slice,
  setIsPhotoSave,
  setIsFullscreen,
  setHomeContainerExpanded,
  setExpandedHomeContainer,
  applyWorkListFilters,
  applyAssetFilters,
  clearAppliedAssetFilters,
  setShowAllTrees,
  clearAppliedWorkListFilters,
  clearAppliedWorkListFiltersForMapNav,
  updateHomePageFilters,
  clearHomePageFilters,
  syncHomeFiltersToMap,
};
